﻿import { FlightStatus } from '@fe/core';
import clsx from 'clsx';
import { CSSProperties } from 'react';

export const statusToReadableString = (status: FlightStatus | null, ground: boolean): string => {
    if (ground) {
        return 'Ground';
    }

    if (!status) {
        return 'Unknown status';
    }

    switch (status) {
        case FlightStatus.Scheduled:
            return 'Scheduled';
        case FlightStatus.Planned:
            return 'Planned';
        case FlightStatus.NewRecord:
            return 'New record';
        case FlightStatus.Departed:
            return 'Departed';
        case FlightStatus.Active:
            return 'In-flight';
        case FlightStatus.Arrived:
            return 'Arrived';
        case FlightStatus.Agedout:
            return 'Aged-out';
        case FlightStatus.Hold:
            return 'Hold';
        default:
            return 'Unknown status';
    }
};

interface FlightStatusBadgeProps {
    flightStatus: FlightStatus;
    ground: boolean;
    style?: CSSProperties;
    className?: string;
    useColorBackground?: boolean;
}

const getFlightBackgroundColor = (ground: boolean, status: FlightStatus, useBgColor: boolean): string => {
    if (!useBgColor) {
        return 'unset';
    }

    if (ground) {
        return 'var(--info-blue)';
    }

    switch (status) {
        case FlightStatus.Departed:
            return 'var(--flight-departed)';
        case FlightStatus.Active:
            return 'var(--flight-in-flight)';
        case FlightStatus.Arrived:
            return 'var(--flight-arrived)';
        case FlightStatus.Agedout:
            return 'var(--flight-aged-out)';
        case FlightStatus.Planned:
            return 'var(--flight-planned)';
        case FlightStatus.Scheduled:
            return 'var(--flight-scheduled)';
        default:
            return '';
    }
};

const getFlightForegroundColor = (ground: boolean, status: FlightStatus, useBgColor: boolean): string => {
    if (!useBgColor) {
        return 'unset';
    }

    if (ground) {
        return 'white';
    }
    if (status === FlightStatus.Arrived || status === FlightStatus.Agedout) {
        return 'white';
    }
    return 'black';
};

export default function FlightStatusBadge({
    flightStatus,
    ground,
    style = {},
    className = '',
    useColorBackground = true
}: FlightStatusBadgeProps) {
    const badgeStyle: CSSProperties = {
        fontSize: '0.8em',
        fontWeight: 500,
        borderRadius: '4px',
        color: getFlightForegroundColor(ground, flightStatus, useColorBackground),
        backgroundColor: getFlightBackgroundColor(ground, flightStatus, useColorBackground),
        padding: clsx(useColorBackground && '0 0.25rem'),
        ...style
    };

    return (
        <span className={className} style={badgeStyle}>
            {statusToReadableString(flightStatus, ground)}
        </span>
    );
}
